// composables/gamification/adapters.ts
//
// Pure display adapters: turn raw `Api*` constellation geometry + a per-member
// fragment balance into the view models the UI binds to. These functions are
// *display adaptation only* — they decide how earned/current/locked state and
// "fragments to next star" are shown. They encode no awarding rules: the member
// fragment balance is an input, computed server-side (M4C-35/36).
//
// Because the same shapes come back from the live endpoints (M4C-30), these
// adapters are reused unchanged once the mock service is swapped for `$fetch`.

import type {
  ActivityRow,
  ApiActionReward,
  ApiActivityRow,
  ApiConstellation,
  ApiConstellationStar,
  ApiGamificationOverview,
  ApiOverviewConstellation,
  ConstellationCardState,
  ConstellationDetail,
  ConstellationMilestone,
  ConstellationOverviewCard,
  ConstellationStarProgress,
  GamificationAction,
  MemberConstellationRow,
  MemberGamificationStats,
  ProfileProgressSummary,
  StarState,
} from '@/types/gamification';

/** A value the API may send as a plain string or a translatable map (`{ en, lt, … }`). */
export type LocalizedText = string | Record<string, string> | null | undefined;

/**
 * Resolve a possibly-translatable value to a display string for `locale`.
 *
 * The public member gamification endpoints (M4C-43) return `category` as a
 * translatable map (e.g. `{ "en": "Community", "lt": "Bendruomenė" }`) rather
 * than a plain string. Mirror `MultilanguageDisplay`'s resolution order — current
 * locale → English → first available → '' — and pass plain strings through
 * unchanged, so the member surfaces render a clean label whichever shape arrives.
 */
export function resolveLocalizedText(value: LocalizedText, locale: string): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return value[locale] ?? value.en ?? Object.values(value)[0] ?? '';
}

/**
 * Resolve the translatable `category` fields on public member stats (M4C-43) so
 * the returned object honors `MemberGamificationStats` (`category: string`) even
 * though the API sends a translatable map. Counts are passed through untouched.
 */
export function localizeMemberStats(
  stats: MemberGamificationStats,
  locale: string,
): MemberGamificationStats {
  return {
    ...stats,
    constellations: stats.constellations.map((c) => ({
      ...c,
      category: resolveLocalizedText(c.category as LocalizedText, locale),
    })),
  };
}

/**
 * Resolve the translatable `category` fields on a public member constellation row
 * (M4C-43) — both the row's own `category` and the nested raw `constellation` —
 * so the returned row honors `MemberConstellationRow` (`category: string`).
 */
export function localizeMemberConstellationRow(
  row: MemberConstellationRow,
  locale: string,
): MemberConstellationRow {
  return {
    ...row,
    category: resolveLocalizedText(row.category as LocalizedText, locale),
    constellation: {
      ...row.constellation,
      category: resolveLocalizedText(row.constellation.category as LocalizedText, locale),
    },
  };
}

/** Route slug for a constellation (`"Lyra Minor"` → `"lyra-minor"`). */
export function slugifyConstellation(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Apply the backend's explicit per-member progress to a constellation's stars.
 *
 * The live API states which stars are earned (`earned_star_ids`) and which one is
 * in progress (`current_star_id`); everything else is `locked`. This is more
 * authoritative than re-deriving from a fragment balance, so the frontend encodes
 * no earning thresholds — it only renders the states the backend reports.
 */
export function deriveStarStatesFromProgress(
  stars: ApiConstellationStar[],
  earnedStarIds: string[],
  currentStarId: string | null,
): ConstellationStarProgress[] {
  const earned = new Set(earnedStarIds);

  return [...stars]
    .sort((a, b) => a.order - b.order)
    .map((star) => {
      let state: StarState;
      if (earned.has(star.id)) {
        state = 'earned';
      } else if (star.id === currentStarId) {
        state = 'current';
      } else {
        state = 'locked';
      }
      return {
        id: star.id,
        order: star.order,
        label: star.label,
        fragments_required: star.fragments_required,
        state,
      };
    });
}

/**
 * Project a raw `gamification/overview` constellation to the `ApiConstellation`
 * geometry shape the renderer/view models expect: drop the per-member `progress`
 * block and resolve the translatable `category` map to `locale`.
 */
function toApiConstellation(api: ApiOverviewConstellation, locale: string): ApiConstellation {
  return {
    id: api.id,
    name: api.name,
    category: resolveLocalizedText(api.category, locale),
    position: api.position,
    default_fragments_required: api.default_fragments_required,
    is_enabled: api.is_enabled,
    stars_count: api.stars_count,
    stars_data: api.stars_data,
    segments_data: api.segments_data,
  };
}

/** Overview card state from the derived stars + balance. */
export function deriveCardState(
  stars: ConstellationStarProgress[],
  currentFragments: number,
): ConstellationCardState {
  const earned = stars.filter((s) => s.state === 'earned').length;
  if (stars.length > 0 && earned === stars.length) {
    return 'collected';
  }
  if (earned === 0 && currentFragments <= 0) {
    return 'not_started';
  }
  return 'in_progress';
}

/** Build an Activity sky overview card from a live `gamification/overview` constellation. */
export function buildOverviewCardFromApi(
  api: ApiOverviewConstellation,
  locale: string,
): ConstellationOverviewCard {
  const stars = deriveStarStatesFromProgress(
    api.stars_data,
    api.progress.earned_star_ids,
    api.progress.current_star_id,
  );
  const constellation = toApiConstellation(api, locale);
  return {
    id: api.id,
    slug: slugifyConstellation(api.name),
    name: constellation.name,
    category: constellation.category,
    position: api.position,
    card_state: deriveCardState(stars, api.progress.current_fragments),
    stars_total: api.stars_count,
    stars_earned: api.progress.completed_stars_count,
    current_fragments: api.progress.current_fragments,
    fragments_to_next_star: api.progress.fragments_to_next_star,
    constellation,
    stars,
  };
}

/** Build the selected-constellation detail from a live `gamification/overview` constellation. */
export function buildConstellationDetailFromApi(
  api: ApiOverviewConstellation,
  actions: GamificationAction[],
  locale: string,
): ConstellationDetail {
  const stars = deriveStarStatesFromProgress(
    api.stars_data,
    api.progress.earned_star_ids,
    api.progress.current_star_id,
  );
  const constellation = toApiConstellation(api, locale);
  const milestones: ConstellationMilestone[] = stars.map((s) => ({
    id: `ms-${s.id}`,
    star_id: s.id,
    label: s.label,
    fragments_required: s.fragments_required,
    achieved: s.state === 'earned',
  }));

  return {
    id: api.id,
    slug: slugifyConstellation(api.name),
    name: constellation.name,
    category: constellation.category,
    card_state: deriveCardState(stars, api.progress.current_fragments),
    constellation,
    stars,
    current_fragments: api.progress.current_fragments,
    fragments_to_next_star: api.progress.fragments_to_next_star,
    next_star: stars.find((s) => s.state === 'current') ?? null,
    milestones,
    actions,
  };
}

/** Map the `gamification/overview` summary to the profile progress view model. */
export function mapOverviewSummary(overview: ApiGamificationOverview): ProfileProgressSummary {
  const { summary, constellations } = overview;
  const inProgress = constellations.filter(
    (c) =>
      c.is_enabled &&
      !c.progress.is_completed &&
      (c.progress.completed_stars_count > 0 || c.progress.current_fragments > 0),
  ).length;
  return {
    fragments_total: summary.total_fragments,
    stars_total: summary.earned_stars_count,
    constellations_total: summary.constellations_count,
    constellations_in_progress: inProgress,
    constellations_collected: summary.completed_constellations_count,
  };
}

/** Map a live `gamification/actions` row to the actions-catalog view model. */
export function mapAction(api: ApiActionReward): GamificationAction {
  return {
    key: api.action,
    label: api.label,
    description: api.description ?? undefined,
    icon: api.icon ?? undefined,
    fragments: api.fragments,
    type: api.type,
  };
}

/**
 * Map a live `gamification/activity` row to the activity view model (best-effort).
 * The feed is empty until the M4C-35/36 runtime produces entries, so this tolerates
 * either an already-`ActivityRow`-shaped payload or the action-style field names
 * from the M4C-30 contract; revisit the field mapping against real data when it lands.
 */
export function mapActivityRow(api: ApiActivityRow): ActivityRow {
  return {
    id: api.id,
    category: api.category,
    title: api.title ?? api.label ?? '',
    description: api.description,
    icon: api.icon,
    occurred_at: api.occurred_at ?? api.created_at ?? '',
    delta_type: api.delta_type ?? (api.stars_delta != null ? 'star' : 'fragments'),
    fragments_delta: api.fragments_delta ?? api.fragments,
    stars_delta: api.stars_delta,
    constellation_id: api.constellation_id,
  };
}
